import { useEffect } from 'react';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@taxdown/zeno-sama-query';
import { PueblaPreviousYearsResponse, SatResponseType } from 'Services/sat/types';
import SatService from 'Services/sat';
import CheckoutService from 'Services/checkout';
import PreviousYearsService from 'Services/previousYears';
import { RequestPreviousYearResponse } from 'Services/previousYears/types';
import { useGetQuery } from 'Hooks/useGetQuery';
import { CustomerType, PreviousYearsPercentage, QueryKeys, ResponseType } from 'Types/common';
import { PreviousYearsResultType } from './types';

export const useFetchGetPreviousYearResultPolling =
  (): UseQueryResult<PueblaPreviousYearsResponse> =>
    useQuery(
      [QueryKeys.CALCULATE_RESULT],
      (): Promise<ResponseType<PueblaPreviousYearsResponse>> => SatService.getPueblaPreviousYears(),
      {
        select: data => {
          const response = data.data;
          return response.item;
        },
        refetchInterval: data => (!data ? 500 : false),
        refetchOnWindowFocus: false,
      }
    );

export const useFetchGetResultPreviousYear = (): PueblaPreviousYearsResponse | undefined => {
  const data = useGetQuery<ResponseType<PueblaPreviousYearsResponse>>([QueryKeys.CALCULATE_RESULT]);
  const response = data?.data;
  return response?.item;
};

export const useFetchGetPreviousYearsPercentage = (): UseMutationResult<
  ResponseType<PreviousYearsPercentage>
> =>
  useMutation(
    [QueryKeys.GET_PREVIOUS_YEAR_PERCENTAGE],
    CheckoutService.getResultPreviousYearsPercentage
  );

export const useGetUserSatInfo = (): UseQueryResult<SatResponseType> =>
  useQuery(
    [QueryKeys.USER_SAT_INFO],
    (): Promise<ResponseType<SatResponseType>> =>
      SatService.getSatUser({ password: true, efirma: true }),
    {
      select: data => {
        const response = data.data;
        return response.item;
      },
      refetchOnWindowFocus: false,
      cacheTime: 0,
    }
  );

export const selectorPreviousYears = (
  previousYears: PueblaPreviousYearsResponse
): PueblaPreviousYearsResponse => {
  if (!previousYears?.compensatedTaxReturns) return previousYears;
  const previousAmount = Math.abs(previousYears.amountPending ?? 0);
  const currentReturnAmount = Math.abs(previousYears?.compensatedTaxReturns?.[0]?.amount ?? 0);
  const isPreviousAmountGreater = previousAmount > currentReturnAmount;

  const remainingAmount = previousAmount - currentReturnAmount;

  return {
    ...previousYears,
    amountPending: isPreviousAmountGreater ? remainingAmount : 0,
    refundStatusType: isPreviousAmountGreater
      ? PreviousYearsResultType.TO_RETURN
      : PreviousYearsResultType.ZERO,
  };
};

export const useFetchGetPreviousYearResult = (): UseQueryResult<PueblaPreviousYearsResponse> =>
  useQuery([QueryKeys.CALCULATE_RESULT_PREVIOUS_YEAR], SatService.getPueblaPreviousYears, {
    select: data => selectorPreviousYears(data.data.item),
    refetchInterval: data => (!data ? 500 : false),
    refetchOnWindowFocus: false,
  });

export const useFetchPreviousYearRequest = (): UseQueryResult<RequestPreviousYearResponse> =>
  useQuery([QueryKeys.PREVIOUS_YEARS_IS_REQUEST], PreviousYearsService.getUserRequestPreviousYear, {
    select: data => {
      const response = data?.data;
      return response?.item;
    },
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });

export const useFetchRequestPreviousYear = (): UseMutationResult<
  unknown,
  unknown,
  unknown,
  unknown
> => useMutation([QueryKeys.REQUEST_PREVIOUS_YEAR], PreviousYearsService.getPreviousYearUser);

export const useAutoRequestPreviousYear = (): void => {
  const { data: requestData, isLoading } = useFetchPreviousYearRequest();
  const { mutate: requestPreviousYear } = useFetchRequestPreviousYear();

  useEffect(() => {
    if (!isLoading && (!requestData || !requestData.isRequested)) {
      requestPreviousYear({});
    }
  }, [requestData, requestPreviousYear, isLoading]);
};

export const useGetUserOneTimeProducts = (): string => {
  const data = useGetQuery<ResponseType<CustomerType>>([QueryKeys.FETCH_GET_CUSTOMERS]);
  const customerInfo = data?.data?.item;
  const oneTimeProducts = customerInfo?.oneTimeProducts;
  const oneTimeProduct = oneTimeProducts?.find(product => product.paymentMethod === 'card');
  return oneTimeProduct?.paymentMethod || '';
};
